/**
 * UK capital gains for individuals, trustees and companies. Share matching (same day → 30 days for individuals/trustees
 * → Section 104 pool, indexed for companies) needs the whole trade history, so it lives in `gbPool.ts`; `computeDisposal`
 * here prices a single slice and is used for what-ifs and corporate-action disposals. Not tax advice.
 * Sources: HMRC CGT rates and allowances https://www.gov.uk/guidance/capital-gains-tax-rates-and-allowances ;
 * CG10245 (rates from 30 Oct 2024) https://www.gov.uk/hmrc-internal-manuals/capital-gains-manual/cg10245 ;
 * Corporation Tax rates https://www.gov.uk/government/publications/rates-and-allowances-corporation-tax ;
 * indexation frozen Dec 2017 for companies https://www.gov.uk/hmrc-internal-manuals/capital-gains-manual/cg17200 .
 */
import { Decimal } from '../money.js';
import { diffDays, financialYearOf } from '../dates.js';
import { KeelError, assertNever } from '../errors.js';
import { gbIndexationFactor } from './gbRpi.js';
/** Individuals (incl. households, sole traders, partners), trustees/personal representatives, companies (incl. clubs). */
export const gbTaxpayer = (entityType) => {
    switch (entityType) {
        case 'individual':
        case 'household':
        case 'sole_trader':
        case 'partnership': return 'individual';
        case 'trust': return 'trust';
        case 'company':
        case 'club': return 'company';
        case 'smsf': throw new KeelError('UNSUPPORTED', 'UK capital gains are not modelled for an Australian SMSF');
        default: return assertNever(entityType, 'entity type');
    }
};
/** UK tax year label ("2024-25") — 6 April to 5 April. */
export const ukTaxYear = (d) => financialYearOf(d, 4, 6).label;
/** Period label for a GB disposal: the UK tax year, or the company's own accounting year (entity FY, default 1 Apr). */
export const gbPeriodLabel = (d, ctx) => gbTaxpayer(ctx.entityType) === 'company' ? financialYearOf(d, ctx.fyStartMonth ?? 4, ctx.fyStartDay ?? 1).label : ukTaxYear(d);
/** Annual exempt amount for individuals, by tax year (pence). */
export const GB_ANNUAL_EXEMPT_AMOUNT = {
    '2016-17': 1_110_000, '2017-18': 1_130_000, '2018-19': 1_170_000, '2019-20': 1_200_000, '2020-21': 1_230_000,
    '2021-22': 1_230_000, '2022-23': 1_230_000, '2023-24': 600_000, '2024-25': 300_000, '2025-26': 300_000,
};
/** Trustees get half the individual amount (£1,500 from 2024-25). ponytail: no split across a settlor's multiple trusts, no vulnerable-beneficiary full amount. */
export const GB_TRUST_ANNUAL_EXEMPT_AMOUNT = Object.fromEntries(Object.entries(GB_ANNUAL_EXEMPT_AMOUNT).map(([y, v]) => [y, Math.floor(v / 2)]));
/** Annual exempt amount for the tax year (later years use the latest amount); companies have none. */
export const gbAnnualExemptAmount = (taxYear, taxpayer = 'individual') => {
    if (taxpayer === 'company')
        return 0;
    const table = taxpayer === 'trust' ? GB_TRUST_ANNUAL_EXEMPT_AMOUNT : GB_ANNUAL_EXEMPT_AMOUNT;
    const keys = Object.keys(table).sort();
    const key = table[taxYear] !== undefined ? taxYear : taxYear > (keys[keys.length - 1] ?? '') ? keys[keys.length - 1] : keys[0];
    return table[key ?? ''] ?? 0;
};
/** Main CGT rates changed for disposals on/after 30 October 2024. */
export const GB_RATE_CHANGE_DATE = '2024-10-30';
/** Shares: individuals 10 %/20 % before 30 Oct 2024, then 18 %/24 %; trustees 20 % then 24 %. */
export const GB_CGT_RATES = {
    individual: { basicBefore: 0.1, higherBefore: 0.2, basic: 0.18, higher: 0.24 },
    trust: { before: 0.2, after: 0.24 },
};
/**
 * CGT / corporation-tax rate on a gain disposed on `date` (default: today's rules).
 * ponytail: individuals' band is picked from `ctx.marginalRate` (≤ 20 % → basic) instead of splitting the gain across the
 * unused basic-rate band; companies pay 25 % main rate or 19 % small profits rate (marginalRate ≤ 0.19), marginal relief ignored.
 */
export const gbCgtRate = (ctx, date = GB_RATE_CHANGE_DATE) => {
    const before = date < GB_RATE_CHANGE_DATE;
    const kind = gbTaxpayer(ctx.entityType);
    switch (kind) {
        case 'individual': {
            const r = GB_CGT_RATES.individual, basic = ctx.marginalRate <= 0.2;
            return before ? (basic ? r.basicBefore : r.higherBefore) : (basic ? r.basic : r.higher);
        }
        case 'trust': return before ? GB_CGT_RATES.trust.before : GB_CGT_RATES.trust.after;
        case 'company': return ctx.marginalRate <= 0.19 ? 0.19 : 0.25;
        default: return assertNever(kind, 'taxpayer');
    }
};
const roundHE = (d) => new Decimal(d).toDecimalPlaces(0, Decimal.ROUND_HALF_EVEN).toNumber();
/**
 * Company indexation allowance on an unindexed gain: cost × factor, limited so it can't create or increase a loss.
 * Returns the gain after indexation and the indexed cost.
 */
export const applyGbIndexation = (proceedsCents, costCents, indexedCostCents) => {
    const unindexed = proceedsCents - costCents;
    if (unindexed <= 0 || indexedCostCents <= costCents)
        return { gainCents: unindexed, indexedCostCents: null };
    return { gainCents: Math.max(0, proceedsCents - indexedCostCents), indexedCostCents };
};
const computeDisposal = (slice, proceedsCents, date, ctx) => {
    const { parcel } = slice;
    const company = gbTaxpayer(ctx.entityType) === 'company';
    const indexed = company ? roundHE(new Decimal(slice.costBaseCents).mul(new Decimal(1).plus(gbIndexationFactor(parcel.acquiredDate, date)))) : slice.costBaseCents;
    const ia = applyGbIndexation(proceedsCents, slice.costBaseCents, indexed);
    return {
        id: `cgt:${slice.tradeId ?? slice.corporateActionId ?? 'adhoc'}:${parcel.id}`,
        entityId: parcel.entityId, accountId: parcel.accountId, securityId: parcel.securityId, parcelId: parcel.id,
        tradeId: slice.tradeId, corporateActionId: slice.corporateActionId, date, acquiredDate: parcel.acquiredDate, units: slice.units,
        proceedsCents, costBaseCents: slice.costBaseCents, reducedCostBaseCents: slice.costBaseCents,
        grossGainCents: ia.gainCents, isLoss: ia.gainCents < 0, discountEligible: false, holdingDays: Math.max(0, diffDays(parcel.acquiredDate, date)),
        method: ia.indexedCostCents === null ? 'other' : 'indexation', indexedCostBaseCents: ia.indexedCostCents,
        financialYear: gbPeriodLabel(date, ctx), createdAt: slice.timestamp, updatedAt: slice.timestamp,
    };
};
/**
 * Year summary. Individuals/trustees: current-year losses set off in full, brought-forward losses only reduce gains above
 * the annual exempt amount. Companies: no exempt amount; current-year then brought-forward capital losses, remainder carried
 * forward (no carry-back). Losses, the exempt amount and b/f losses are set against post-30-Oct-2024 gains first
 * (HMRC lets them be set in the most beneficial way).
 */
export const summariseGbYear = (events, carriedForwardInCents, ctx, opts = {}) => {
    const taxpayer = gbTaxpayer(ctx.entityType);
    const evs = opts.fy ? events.filter((e) => e.financialYear === opts.fy) : [...events];
    const fy = opts.fy ?? evs[0]?.financialYear ?? '';
    const totalGains = evs.reduce((t, e) => t + Math.max(0, e.grossGainCents), 0);
    const totalLosses = evs.reduce((t, e) => t + Math.max(0, -e.grossGainCents), 0);
    const gainsAfter = evs.filter((e) => e.date >= GB_RATE_CHANGE_DATE).reduce((t, e) => t + Math.max(0, e.grossGainCents), 0);
    const cfIn = Math.max(0, carriedForwardInCents);
    const aea = gbAnnualExemptAmount(fy, taxpayer);
    const net = totalGains - totalLosses;
    const aeaUsed = net > 0 ? Math.min(aea, net) : 0;
    const cfUsed = net > 0 ? Math.min(cfIn, net - aeaUsed) : 0;
    const taxable = net > 0 ? net - aeaUsed - cfUsed : 0;
    const cfOut = cfIn - cfUsed + Math.max(0, -net);
    const after = Math.min(taxable, Math.max(0, gainsAfter - (totalGains - taxable)));
    return {
        fy, grossGainsCents: totalGains, trustCapitalGainsCents: 0, discountableGainsCents: 0, nonDiscountGainsCents: totalGains,
        currentYearLossesCents: totalLosses, lossesAppliedCents: Math.min(totalLosses, totalGains), carriedForwardInCents: cfIn,
        carriedForwardAppliedCents: cfUsed, discountCents: 0, netCapitalGainCents: taxable, carriedForwardOutCents: cfOut,
        postRegimeGainCents: 0, regime2027: null,
        atoLabels: { '18H': totalGains, '18A': taxable, '18V': cfOut },
        events: evs, jurisdiction: 'GB', taxpayer, annualExemptAmountCents: aea,
        taxableBeforeRateChangeCents: taxable - after, taxableAfterRateChangeCents: after,
        labels: { totalGains, totalLosses, netGain: Math.max(0, net), annualExemptAmountUsed: aeaUsed, taxableGain: taxable, carriedForwardOut: cfOut },
    };
};
const isGbSummary = (s) => s.jurisdiction === 'GB' && 'taxableAfterRateChangeCents' in s;
export const gbJurisdiction = {
    code: 'GB',
    discountRate: () => 0,
    isDiscountEligible: () => false,
    computeDisposal,
    summariseYear: summariseGbYear,
    estimateTax: (s, ctx) => {
        const taxable = Math.max(0, s.netCapitalGainCents);
        const before = isGbSummary(s) ? s.taxableBeforeRateChangeCents : 0;
        return roundHE(new Decimal(taxable - before).mul(gbCgtRate(ctx)).plus(new Decimal(before).mul(gbCgtRate(ctx, '2024-10-29'))));
    },
    marginalGainRate: (ctx) => gbCgtRate(ctx),
};
//# sourceMappingURL=gb.js.map